import { useCallback, useEffect, useState } from 'react'
import { Unauthorized, api, getToken, setToken, type HealthReport } from './api'
import { TABS, routeForTab, type Route, type Tab } from './route'
import { useRoute } from './useRoute'
import { OverviewTab } from './tabs/Overview'
import { CampaignsTab } from './tabs/Campaigns'
import { FiltersTab } from './tabs/Filters'
import { PeopleTab } from './tabs/People'
import { AccountsTab } from './tabs/Accounts'
import { SettingsTab } from './tabs/Settings'
import { EmailsTab } from './tabs/Emails'
import { ImprovementTab } from './tabs/Improvement'
import { StylesTab } from './tabs/Styles'
import { LibraryTab } from './tabs/Library'

/**
 * The shell.
 *
 * Home is Campaigns (D83): the standing programs, each with its filter, copy,
 * prompt, schedule, cooldown and send policy, and the cycles they have run.
 * Filters is where an audience is defined; People is where one is counted and
 * a saved list looked at. The rest are reference screens.
 *
 * The three-step wizard is gone. A cycle carries the compose, refine, review
 * and send surfaces that used to be its steps, and a person reaches them from
 * the campaign that owns the cycle.
 *
 * Screens are real URLs (`/campaigns/3/cycles/12`, …). Without that, Back left
 * the app because nothing had been pushed onto the history stack.
 */

/** How often the header re-checks both databases. */
const HEALTH_POLL_MS = 20_000

type AuthCheckResult = 'authed' | 'rejected' | 'other'

export default function App() {
  const { route, navigate, goToTab, goBack } = useRoute()
  const tab = route.tab
  const [health, setHealth] = useState<HealthReport | null>(null)
  const [authed, setAuthed] = useState<boolean | null>(null)
  const [tokenRejected, setTokenRejected] = useState(false)

  // Tabs are mounted on first visit and then KEPT mounted, hidden when
  // inactive, because unmounting them threw away work — a chosen filter, a
  // scan in flight, a review selection.
  const [visited, setVisited] = useState<Set<Tab>>(() => new Set([tab]))

  // The one handoff left: a filter on its way to People to be counted. Cleared
  // by the tab that consumes it, so it does not re-select on a later return.
  const [pendingFilter, setPendingFilter] = useState<string | null>(null)

  useEffect(() => {
    setVisited((seen) => (seen.has(tab) ? seen : new Set(seen).add(tab)))
  }, [tab])

  const checkHealth = useCallback(async () => {
    setHealth(await api.healthReport())
  }, [])

  const check = useCallback(async (): Promise<AuthCheckResult> => {
    // Authorisation first and on its own: whether the token works has nothing
    // to do with whether a database is reachable.
    try {
      await api.filters()
      setAuthed(true)
      setTokenRejected(false)
      return 'authed'
    } catch (err) {
      if (err instanceof Unauthorized) {
        setAuthed(false)
        setTokenRejected(true)
        return 'rejected'
      }
      setAuthed(true)
      setTokenRejected(false)
      return 'other'
    } finally {
      await checkHealth()
    }
  }, [checkHealth])

  useEffect(() => {
    void check()
  }, [check])

  useEffect(() => {
    const id = window.setInterval(() => void checkHealth(), HEALTH_POLL_MS)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkHealth()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [checkHealth])

  if (authed === null) return <div className="loading">Connecting…</div>
  if (!authed) {
    return (
      <TokenGate
        rejected={tokenRejected}
        onEdit={() => setTokenRejected(false)}
        onSubmit={check}
      />
    )
  }

  return (
    <div className="shell">
      <header className="top">
        <span className="brand">Reach-Out</span>
        <HealthStrip report={health} />
        <nav className="tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              aria-current={tab === t.id}
              onClick={() => goToTab(t.id)}
              title={t.title}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>
      <main>
        {TABS.filter((t) => visited.has(t.id)).map((t) => (
          <div key={t.id} hidden={t.id !== tab}>
            <Pane
              id={t.id}
              active={t.id === tab}
              route={route}
              goToTab={goToTab}
              navigate={navigate}
              goBack={goBack}
              pendingFilter={pendingFilter}
              setPendingFilter={setPendingFilter}
            />
          </div>
        ))}
      </main>
    </div>
  )
}

function Pane({
  id,
  active,
  route,
  goToTab,
  navigate,
  goBack,
  pendingFilter,
  setPendingFilter,
}: {
  id: Tab
  active: boolean
  route: Route
  goToTab: (tab: Tab) => void
  navigate: (next: Route, opts?: { replace?: boolean; back?: Route }) => void
  goBack: (fallback: Route) => void
  pendingFilter: string | null
  setPendingFilter: (name: string | null) => void
}) {
  switch (id) {
    case 'overview':
      return <OverviewTab onGoTo={(next) => goToTab(next as Tab)} />
    case 'campaigns':
      return (
        <CampaignsTab
          active={active}
          campaignId={active ? route.campaignId : undefined}
          cycleId={active ? route.cycleId : undefined}
          creating={active ? route.creating : undefined}
          onOpenCampaign={(campaignId) =>
            navigate(
              { ...routeForTab('campaigns'), campaignId },
              { back: routeForTab('campaigns') },
            )
          }
          onOpenCycle={(cycleId, campaignId) =>
            navigate(
              { ...routeForTab('campaigns'), campaignId, cycleId },
              { back: { ...routeForTab('campaigns'), campaignId } },
            )
          }
          onNewCampaign={() =>
            navigate(
              { ...routeForTab('campaigns'), creating: true },
              { back: routeForTab('campaigns') },
            )
          }
          onCloseCampaign={() => goBack(routeForTab('campaigns'))}
          onCloseCycle={(campaignId) =>
            goBack({ ...routeForTab('campaigns'), campaignId })
          }
          onCancelNew={() => goBack(routeForTab('campaigns'))}
          onCreated={(campaignId) =>
            navigate({ ...routeForTab('campaigns'), campaignId }, { replace: true })
          }
          onDeleted={() => navigate(routeForTab('campaigns'), { replace: true })}
          onGoToFilters={() => goToTab('filters')}
        />
      )
    case 'filters':
      return (
        <FiltersTab
          active={active}
          selected={active ? route.filter : undefined}
          onSelect={(name) => navigate({ ...routeForTab('filters'), filter: name })}
          onDeleted={() => navigate(routeForTab('filters'), { replace: true })}
          onRunFilter={(name) => {
            setPendingFilter(name)
            goToTab('people')
          }}
        />
      )
    case 'people':
      return (
        <PeopleTab
          active={active}
          preselect={pendingFilter}
          onPreselectUsed={() => setPendingFilter(null)}
        />
      )
    case 'accounts':
      return <AccountsTab active={active} />
    case 'emails':
      return (
        <EmailsTab
          active={active}
          draftId={active ? route.draftId : undefined}
          onOpenDraft={(draftId) =>
            navigate({ ...routeForTab('emails'), draftId }, { back: routeForTab('emails') })
          }
          onCloseDraft={() => goBack(routeForTab('emails'))}
        />
      )
    case 'styles':
      return <StylesTab active={active} />
    case 'library':
      return <LibraryTab active={active} />
    case 'improvement':
      return <ImprovementTab />
    case 'settings':
      return <SettingsTab />
  }
}

function dotClass(status: string): 'ok' | 'busy' | 'bad' {
  if (status.startsWith('ok')) return 'ok'
  if (status.startsWith('busy')) return 'busy'
  return 'bad'
}

function ago(at: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - at) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  return `${Math.round(seconds / 60)}m ago`
}

function HealthStrip({ report }: { report: HealthReport | null }) {
  if (!report) return null

  if (report.state === 'unreachable') {
    return (
      <div className="health">
        <span
          title={
            `The server is not answering: ${report.error}. ` +
            `Checked ${ago(report.at)}. Reload once it is back.`
          }
        >
          <span className="dot bad" />
          server unreachable
        </span>
      </div>
    )
  }

  const health = report.health
  return (
    <div className="health">
      {Object.entries(health.databases).map(([name, status]) => (
        <span key={name} title={`${status} (checked ${ago(report.at)})`}>
          <span className={`dot ${dotClass(status)}`} />
          {name}
        </span>
      ))}
      <span
        title={
          health.send_enabled
            ? 'REACHOUT_ALLOW_SEND is 1 on the GUI process. A customer send is still refused unless every blocker on the cycle’s Send panel is clear. The scheduler reads its own environment.'
            : 'REACHOUT_ALLOW_SEND is not 1 on the GUI process, so no manual customer send can be dispatched from here.'
        }
      >
        <span className={`dot ${health.send_enabled ? 'bad' : 'ok'}`} />
        send {health.send_enabled ? 'live' : 'off'}
      </span>
    </div>
  )
}

function TokenGate({
  rejected,
  onEdit,
  onSubmit,
}: {
  rejected: boolean
  onEdit: () => void
  onSubmit: () => Promise<AuthCheckResult>
}) {
  const [value, setValue] = useState(getToken())
  const [busy, setBusy] = useState(false)

  return (
    <div className="gate">
      <div className="panel">
        <h2>Access token</h2>
        <div className="panel-body">
          <p className="hint">
            The API is guarded by a shared token — the value of{' '}
            <span className="mono">REACHOUT_GUI_TOKEN</span> on the server. It is kept in this
            browser only and sent as a header.
          </p>
          {rejected && (
            <div className="error">
              Token rejected — it does not match <span className="mono">REACHOUT_GUI_TOKEN</span>{' '}
              on this server. For local dev the value is usually{' '}
              <span className="mono">local-dev</span>.
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (busy) return
              setToken(value.trim())
              setBusy(true)
              void onSubmit().finally(() => setBusy(false))
            }}
          >
            <div className="field">
              <input
                type="text"
                value={value}
                autoFocus
                disabled={busy}
                onChange={(e) => {
                  setValue(e.target.value)
                  onEdit()
                }}
                placeholder="paste token"
              />
            </div>
            <button className="primary" type="submit" disabled={busy || !value.trim()}>
              {busy ? 'Checking…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
